import { isSabUsable, SabMessageChannel, } from '@streamr/tv-engine';
import { EventEmitter } from 'eventemitter3';
import { isGapDiagnosticsEnabled, logGapDiagnostic, logGapDiagnosticSampled, } from '../GapDiagnostics';
import { MetricsManager } from '../MetricsManager';
import * as audioPlayerProcessor from './multiaudioplayer.worklet';
const SAB_USABLE = isSabUsable();
// SAB-backed decoder→worklet PCM path on every COI-isolated browser (was
// previously WebKit-only). The MessagePort path remains as a fallback for
// non-COI hosts.
const USE_DECODER_SAB = SAB_USABLE;
// SAB sync path moves the 50 Hz worklet -> VideoPlayerWorker "played" event
// off the main thread on every COI-isolated environment (Chrome and Safari).
// The MessagePort path remains as a fallback for non-COI hosts.
const USE_SYNC_SAB = SAB_USABLE;
export class AudioPlayer extends EventEmitter {
    audioWorkletPromise;
    audioContext;
    getOptimalDelayCallback;
    mainGainNode;
    trackNodes = new Map();
    isPlaying = false;
    // Audio capture state
    captureDestination;
    captureInputSources = new Map();
    // Per-guest N-1 captures, keyed by capture id (the guest's user id)
    n1Captures = new Map();
    // Content audio stopping: when active, content tracks are fully removed
    contentStopExemptTrackIds = new Set();
    contentAudioStopped = false;
    constructor(audioContext, getOptimalDelayCallback) {
        super();
        this.audioContext = audioContext;
        if (audioContext.state === 'running') {
            this.isPlaying = true;
        }
        audioContext.addEventListener('statechange', () => {
            if (audioContext.state === 'running') {
                this.isPlaying = true;
                this.startPendingTracks();
            }
            if (audioContext.state === 'suspended') {
                this.isPlaying = false;
            }
        });
        this.getOptimalDelayCallback = getOptimalDelayCallback;
        this.mainGainNode = this.audioContext.createGain();
        this.mainGainNode.connect(this.audioContext.destination);
    }
    startPendingTracks() {
        for (const [trackId, trackNode] of this.trackNodes) {
            if (trackNode.isPlaying)
                continue;
            const optimalDelayMillis = this.getOptimalDelayCallback(trackId);
            if (optimalDelayMillis !== undefined) {
                trackNode.isPlaying = true;
                trackNode.workletNode.port.postMessage({
                    type: 'startPlayback',
                    optimalDelayMillis,
                });
            }
        }
    }
    /**
     * Feed a decoded audio packet. Creates a new worklet node when trackId changes.
     */
    feedAudioPacket(packet) {
        if (this.audioContext.state === 'closed') {
            return;
        }
        let trackNode = this.trackNodes.get(packet.trackId);
        if (!trackNode) {
            trackNode = this.createNewWorkletNode(packet.trackId);
            this.trackNodes.set(packet.trackId, trackNode);
            this.emit('trackAdded', packet.trackId);
        }
        // Get current optimal delay (may be undefined if not yet calculated)
        const optimalDelayMillis = this.getOptimalDelayCallback(packet.trackId);
        // Start playback when optimal delay becomes available
        if (this.isPlaying &&
            !trackNode.isPlaying &&
            optimalDelayMillis !== undefined) {
            console.log('starting audio playback at optimal delay', optimalDelayMillis, packet.trackId);
            trackNode.isPlaying = true;
        }
        // Feed audio frame with current optimal delay piggybacked
        trackNode.workletNode.port.postMessage({
            type: 'feedAudioFrame',
            decodedAudioData: packet.audioData,
            optimalDelayMillis, // Piggyback optimal delay with every frame
            startPlayback: trackNode.isPlaying,
        }, packet.audioData.channels.map((channel) => channel.buffer));
        /**
         * @todo Fix `endOfTrack` handling. We `postMessage` audio data but then `removeTrack`
         * immediately, disconnecting the worklet before it can play. Since `postMessage` is
         * async, `endOfTrack` audio may be buffered but never heard.
         */
        if (packet.endOfTrack) {
            this.removeTrack(packet.trackId);
        }
    }
    async init() {
        if (!this.audioWorkletPromise) {
            this.audioWorkletPromise = this.audioContext.audioWorklet
                .addModule(
            // biome-ignore lint/suspicious/noExplicitAny: @todo fix types (the "any")
            audioPlayerProcessor)
                .catch((e) => {
                console.error('[AudioPlayer] addModule(audio-player) failed:', e);
                throw e;
            });
        }
        await this.audioWorkletPromise;
    }
    // This will start audio playback `optimalDelayMillis` milliseconds
    // from the end of the audio buffer, if there is enough data
    // in the buffer. If there is not enough data, it will start
    // buffering the audio and then start playback when enough data
    // is available.
    /*
    public startAtOptimalDelayMillis(optimalDelayMillis: number): void {
        if (this.optimalDelayMillis === optimalDelayMillis) {
            return;
        }

        this.optimalDelayMillis = optimalDelayMillis;

        for (const trackNode of this.trackNodes.values()) {
            trackNode.workletNode.port.postMessage({
                type: 'startAtOptimalDelayMillis',
                optimalDelayMillis,
            });
        }
    }
    */
    flushAllTracks() {
        for (const trackNode of this.trackNodes.values()) {
            trackNode.workletNode.port.postMessage({ type: 'flush' });
        }
    }
    async reset() {
        const tasks = [];
        for (const trackNode of this.trackNodes.values()) {
            const promise = new Promise((resolve) => {
                const handler = (e) => {
                    if (e.data.type === 'resetCompleted') {
                        trackNode.workletNode.port.removeEventListener('message', handler);
                        resolve();
                    }
                };
                trackNode.workletNode.port.addEventListener('message', handler);
                trackNode.workletNode.port.postMessage({ type: 'reset' });
            });
            tasks.push(promise);
        }
        await Promise.all(tasks);
        this.emit('workletResetCompleted');
    }
    async dispose() {
        this.removeAllN1Captures();
        for (const trackId of this.trackNodes.keys()) {
            this.removeTrack(trackId);
        }
        await this.audioContext.close();
    }
    /**
     * Creates a new audio worklet node and gain node for a specific track.
     *
     * This method assumes that `init()` has been called and awaited before invocation,
     * ensuring the worklet module is properly loaded and the AudioContext is initialized.
     */
    createNewWorkletNode(trackId) {
        const workletNode = new AudioWorkletNode(this.audioContext, 'audio-player', {
            outputChannelCount: [1],
            processorOptions: {
                gapDiagnosticsEnabled: isGapDiagnosticsEnabled(),
            },
        });
        const gainNode = this.audioContext.createGain();
        workletNode.connect(gainNode);
        gainNode.connect(this.mainGainNode);
        for (const n1 of this.n1Captures.values()) {
            if (!n1.excludedTrackIds.has(trackId)) {
                gainNode.connect(n1.destination);
                n1.connectedTrackGains.set(trackId, gainNode);
            }
        }
        workletNode.port.onmessage = (e) => {
            if (e.data.type === 'purged') {
                // The 'purged' message should not be removed even though it is not currently used.
                // It is used for moving the AudioData from the worklet to the main thread, where it can be garbage-collected.
            }
            if (e.data.type === 'played') {
                // When the SAB sync path is active, the worklet routes its
                // 50 Hz played events directly to VideoPlayerWorker via
                // SharedArrayBuffer — bypassing this main-thread handler
                // entirely. Any `played` MessagePort frame that still
                // arrives is from a worklet that has not (yet) been
                // wired to the SAB sync path, e.g. early arrivals before
                // `setSyncSabHandle` is delivered. Relay those too so the
                // first frame still drives PlaybackClock.
                const playedSentAt = e.data.realTimeMillis;
                if (typeof playedSentAt === 'number') {
                    const deliveryDelayMs = Date.now() - playedSentAt;
                    logGapDiagnosticSampled('audioPlayer.playedDeliveryDelay', {
                        trackId,
                        deltaMs: deliveryDelayMs,
                    });
                }
                const presentationTimeMicros = Number(e.data.frame.timestamp) +
                    Number(e.data.frame.duration);
                const trackNode = this.trackNodes.get(trackId);
                if (trackNode) {
                    this.emit('newReferenceTimes', trackId, presentationTimeMicros, e.data.realTimeMillis);
                }
            }
            if (e.data.type === 'playbackStarted') {
                this.emit('playbackStarted', trackId);
            }
            if (e.data.type === 'bufferMetrics') {
                MetricsManager.getInstance().logMetric('audioBufferLengthMillisecondsTrack ' + trackId, e.data.bufferLengthMs);
                logGapDiagnostic('audioPlayer.bufferMetrics', {
                    trackId,
                    detail: {
                        bufferLengthMs: e.data.bufferLengthMs,
                        optimalDelayMs: e.data.optimalDelayMs,
                    },
                });
            }
            if (e.data.type === 'underflow') {
                logGapDiagnostic('audioPlayer.underflow', {
                    trackId,
                    detail: {
                        bufferLengthMs: e.data.bufferLengthMs,
                        optimalDelayMs: e.data.optimalDelayMs,
                    },
                });
                this.emit('audioUnderflow', trackId);
            }
            if (e.data.type === 'debug') {
                // Forward debug messages from worklet
                //console.log(`[AudioWorklet] ${e.data.message}`);
            }
            if (e.data.type === 'resetCompleted') {
                // Per-track reset completion is handled in reset() method
            }
        };
        return {
            workletNode,
            gainNode,
            trackId,
            isPlaying: false,
        };
    }
    /**
     * Create a worklet node for a track whose decoded audio arrives via
     * a MessagePort from AudioDecoderWorker (two-port design).
     * Returns:
     *  - `decoderPort`: one end of the MessagePort / SAB channel — to be
     *    transferred to the AudioDecoderWorker for that track.
     *  - `syncPortHandle`: when SAB sync is active (COI environments),
     *    the remote end of a per-track SAB ring connecting the
     *    AudioWorklet directly to the VideoPlayerWorker. The caller
     *    forwards it to the VideoPlayerWorker via Comlink. When sync SAB
     *    is unavailable, this is undefined and the main-thread MessagePort
     *    fallback drives playback sync.
     */
    createTrackForDecoderPort(trackId) {
        let trackNode = this.trackNodes.get(trackId);
        if (!trackNode) {
            trackNode = this.createNewWorkletNode(trackId);
            this.trackNodes.set(trackId, trackNode);
            this.emit('trackAdded', trackId);
        }
        let decoderPort;
        if (USE_DECODER_SAB) {
            const sab = new SabMessageChannel({
                capacity: 512 * 1024,
                name: 'audio-dec<->worklet',
            });
            trackNode.workletNode.port.postMessage({
                type: 'setDecoderSabHandle',
                handle: sab.port1.getHandle(),
            });
            decoderPort = sab.port2.getHandle();
            console.log('[AudioPlayer] createTrackForDecoderPort: using SAB pipe to worklet', { trackId: trackId.slice(0, 8) });
        }
        else {
            const channel = new MessageChannel();
            trackNode.workletNode.port.postMessage({ type: 'setDecoderPort', port: channel.port1 }, [channel.port1]);
            decoderPort = channel.port2;
        }
        let syncPortHandle;
        if (USE_SYNC_SAB) {
            // 32 KiB capacity: at ~70 bytes per encoded sync message, this
            // holds ~450 events (~9 s of 50 Hz audio). Overflow drops the
            // oldest played event — PlaybackClock's wall-clock fallback at
            // 30 ms staleness covers any gaps.
            const syncSab = new SabMessageChannel({
                capacity: 32 * 1024,
                name: 'sync.worklet->vpw',
            });
            // The worklet sends on port1.sendSab; VideoPlayerWorker drains
            // port2 which reads from that same SAB. We hand the worklet
            // port1's handle (so its `sendSab` is the producer ring) and
            // hand VideoPlayerWorker port2's handle (its `recvSab` is the
            // same producer ring). See SabMessageChannel docs.
            trackNode.workletNode.port.postMessage({
                type: 'setSyncSabHandle',
                handle: syncSab.port1.getHandle(),
                trackId: trackId,
            });
            syncPortHandle = syncSab.port2.getHandle();
            this.emit('audioSyncSabReady', trackId, syncPortHandle);
        }
        const optimalDelayMillis = this.getOptimalDelayCallback(trackId);
        if (this.isPlaying && optimalDelayMillis !== undefined) {
            trackNode.isPlaying = true;
            trackNode.workletNode.port.postMessage({
                type: 'startPlayback',
                optimalDelayMillis,
            });
        }
        return { decoderPort, syncPortHandle };
    }
    /**
     * Send an updated optimal delay to a track's worklet (control-only, no data).
     */
    updateTrackOptimalDelay(trackId) {
        const trackNode = this.trackNodes.get(trackId);
        if (!trackNode)
            return;
        const optimalDelayMillis = this.getOptimalDelayCallback(trackId);
        if (optimalDelayMillis === undefined)
            return;
        if (!trackNode.isPlaying && this.isPlaying) {
            trackNode.isPlaying = true;
            trackNode.workletNode.port.postMessage({
                type: 'startPlayback',
                optimalDelayMillis,
            });
        }
        else {
            trackNode.workletNode.port.postMessage({
                type: 'optimalDelayMillis',
                optimalDelayMillis,
            });
        }
    }
    removeTrack(trackId) {
        const trackNode = this.trackNodes.get(trackId);
        if (!trackNode) {
            return;
        }
        trackNode.workletNode.port.onmessage = null;
        for (const n1 of this.n1Captures.values()) {
            n1.connectedTrackGains.delete(trackId);
        }
        try {
            trackNode.workletNode.disconnect();
            trackNode.gainNode.disconnect();
        }
        catch (_) {
            // ignore
        }
        this.trackNodes.delete(trackId);
        this.emit('trackRemoved', trackId);
    }
    getActiveTrackIds() {
        return [...this.trackNodes.keys()];
    }
    /**
     * Set the gain for a specific track (0.0 to 1.0+).
     */
    setTrackGain(trackId, gain) {
        const trackNode = this.trackNodes.get(trackId);
        if (trackNode && trackNode.gainNode.gain.value !== gain) {
            trackNode.gainNode.gain.value = gain;
            this.emit('trackGainChanged', trackId, gain);
        }
    }
    getTrackGain(trackId) {
        const trackNode = this.trackNodes.get(trackId);
        return trackNode?.gainNode.gain.value ?? 1;
    }
    /**
     * Set the main output gain (0.0 to 1.0+).
     */
    setMainGain(gain) {
        if (this.mainGainNode.gain.value !== gain) {
            this.mainGainNode.gain.value = gain;
            this.emit('mainGainChanged', gain);
        }
    }
    /**
     * Stop all content audio tracks (composite audio from the content
     * partition). Tracks are fully removed, not just gain-muted.
     * Returns the list of removed trackIds so the caller can clean up
     * decoders and mappers.
     */
    stopContentAudioTracks() {
        this.contentAudioStopped = true;
        const removed = [];
        for (const trackId of [...this.trackNodes.keys()]) {
            if (!this.contentStopExemptTrackIds.has(trackId)) {
                this.removeTrack(trackId);
                removed.push(trackId);
            }
        }
        console.log(`[AudioPlayer] Content audio stopped (${removed.length} tracks removed)`);
        return removed;
    }
    resumeContentAudioTracks() {
        this.contentStopExemptTrackIds.clear();
        this.contentAudioStopped = false;
        console.log('[AudioPlayer] Content audio resumed');
    }
    isContentAudioStopped() {
        return this.contentAudioStopped;
    }
    /**
     * Mark a track as exempt from content-audio stopping (e.g. N-1 audio
     * from the one-to-one stream). Exempt tracks are kept alive when
     * stopContentAudioTracks is called.
     */
    exemptTrackFromContentStop(trackId) {
        this.contentStopExemptTrackIds.add(trackId);
    }
    /**
     * Connect an input MediaStreamTrack (e.g. the broadcaster's own mic)
     * directly to the capture destination, bypassing the speaker output
     * so the broadcaster doesn't hear their own voice with delay.
     */
    addInputSourceToCapture(track) {
        if (!this.captureDestination) {
            console.warn('[AudioPlayer] Cannot add input source: capture not started');
            return;
        }
        if (this.captureInputSources.has(track.id)) {
            return;
        }
        const sourceNode = this.audioContext.createMediaStreamSource(new MediaStream([track]));
        const gainNode = this.audioContext.createGain();
        sourceNode.connect(gainNode);
        gainNode.connect(this.captureDestination);
        this.captureInputSources.set(track.id, {
            source: sourceNode,
            gain: gainNode,
        });
        for (const n1 of this.n1Captures.values()) {
            const n1Source = this.audioContext.createMediaStreamSource(new MediaStream([track]));
            const n1Gain = this.audioContext.createGain();
            n1Source.connect(n1Gain);
            n1Gain.connect(n1.destination);
            n1.connectedInputSources.set(track.id, {
                source: n1Source,
                gain: n1Gain,
            });
        }
    }
    removeInputSourceFromCapture(trackId) {
        const entry = this.captureInputSources.get(trackId);
        if (entry) {
            try {
                entry.source.disconnect();
                entry.gain.disconnect();
            }
            catch (_) {
                // already disconnected
            }
            this.captureInputSources.delete(trackId);
        }
        for (const n1 of this.n1Captures.values()) {
            const n1Entry = n1.connectedInputSources.get(trackId);
            if (n1Entry) {
                try {
                    n1Entry.source.disconnect();
                    n1Entry.gain.disconnect();
                }
                catch (_) {
                    // already disconnected
                }
                n1.connectedInputSources.delete(trackId);
            }
        }
    }
    setInputSourceCaptureGain(trackId, gain) {
        const entry = this.captureInputSources.get(trackId);
        if (entry) {
            entry.gain.gain.setValueAtTime(gain, this.audioContext.currentTime);
        }
    }
    /**
     * Start capturing the mixed audio output.
     * Returns a MediaStreamTrack that can be used for encoding.
     */
    startAudioCapture() {
        if (this.captureDestination) {
            console.warn('[AudioPlayer] Audio capture already started, returning existing track');
            return this.captureDestination.stream.getAudioTracks()[0];
        }
        // Create destination node that outputs to a MediaStream
        this.captureDestination =
            this.audioContext.createMediaStreamDestination();
        // Connect mainGainNode to capture destination (in addition to speakers)
        this.mainGainNode.connect(this.captureDestination);
        console.log('[AudioPlayer] Audio capture started');
        return this.captureDestination.stream.getAudioTracks()[0];
    }
    /**
     * Create an N-1 capture that mixes all current audio sources EXCEPT the
     * inbound tracks in `excludedTrackIds` — every audio track of the guest
     * the capture is for (microphone, shared-tab audio, …). Returns a
     * MediaStreamTrack whose output can be fed into an encoder.
     *
     * The capture automatically stays in sync: when new tracks or input
     * sources are added/removed, the N-1 graph is updated via hooks in
     * createNewWorkletNode, removeTrack, addInputSourceToCapture, and
     * removeInputSourceFromCapture. The exclusion set itself follows the
     * guest's tracks through setN1ExcludedTracks.
     */
    createN1Capture(captureId, excludedTrackIds) {
        const existing = this.n1Captures.get(captureId);
        if (existing) {
            console.warn(`[AudioPlayer] N-1 capture already exists for ${captureId}`);
            this.setN1ExcludedTracks(captureId, excludedTrackIds);
            return existing.destination.stream.getAudioTracks()[0];
        }
        const excluded = new Set(excludedTrackIds);
        const destination = this.audioContext.createMediaStreamDestination();
        const connectedTrackGains = new Map();
        const connectedInputSources = new Map();
        for (const [trackId, trackNode] of this.trackNodes) {
            if (!excluded.has(trackId)) {
                trackNode.gainNode.connect(destination);
                connectedTrackGains.set(trackId, trackNode.gainNode);
            }
        }
        for (const [inputId, entry] of this.captureInputSources) {
            const n1Source = this.audioContext.createMediaStreamSource(new MediaStream([entry.source.mediaStream.getAudioTracks()[0]]));
            const n1Gain = this.audioContext.createGain();
            n1Gain.gain.value = entry.gain.gain.value;
            n1Source.connect(n1Gain);
            n1Gain.connect(destination);
            connectedInputSources.set(inputId, {
                source: n1Source,
                gain: n1Gain,
            });
        }
        this.n1Captures.set(captureId, {
            destination,
            excludedTrackIds: excluded,
            connectedTrackGains,
            connectedInputSources,
        });
        console.log(`[AudioPlayer] N-1 capture created for ${captureId}, excluding tracks ${[...excluded].join(', ') || '(none)'}`);
        return destination.stream.getAudioTracks()[0];
    }
    /**
     * Replace the set of inbound tracks kept out of an N-1 capture: tracks
     * that become excluded are disconnected from the mix, tracks that stop
     * being excluded (and are playing) are connected. Returns false when
     * there is no such capture.
     */
    setN1ExcludedTracks(captureId, excludedTrackIds) {
        const n1 = this.n1Captures.get(captureId);
        if (!n1)
            return false;
        const next = new Set(excludedTrackIds);
        for (const trackId of next) {
            const gainNode = n1.connectedTrackGains.get(trackId);
            if (!gainNode)
                continue;
            try {
                gainNode.disconnect(n1.destination);
            }
            catch (_) {
                // already disconnected
            }
            n1.connectedTrackGains.delete(trackId);
        }
        for (const trackId of n1.excludedTrackIds) {
            if (next.has(trackId))
                continue;
            const trackNode = this.trackNodes.get(trackId);
            if (trackNode && !n1.connectedTrackGains.has(trackId)) {
                trackNode.gainNode.connect(n1.destination);
                n1.connectedTrackGains.set(trackId, trackNode.gainNode);
            }
        }
        n1.excludedTrackIds = next;
        console.log(`[AudioPlayer] N-1 capture ${captureId} now excludes tracks ${[...next].join(', ') || '(none)'}`);
        return true;
    }
    removeN1Capture(captureId) {
        const n1 = this.n1Captures.get(captureId);
        if (!n1)
            return;
        for (const [, gainNode] of n1.connectedTrackGains) {
            try {
                gainNode.disconnect(n1.destination);
            }
            catch (_) {
                // already disconnected
            }
        }
        for (const [, entry] of n1.connectedInputSources) {
            try {
                entry.source.disconnect();
                entry.gain.disconnect();
            }
            catch (_) {
                // already disconnected
            }
        }
        const tracks = n1.destination.stream.getAudioTracks();
        for (const track of tracks) {
            track.stop();
        }
        this.n1Captures.delete(captureId);
        console.log(`[AudioPlayer] N-1 capture removed for ${captureId}`);
    }
    removeAllN1Captures() {
        for (const captureId of [...this.n1Captures.keys()]) {
            this.removeN1Capture(captureId);
        }
    }
    /**
     * Stop capturing the mixed audio output.
     */
    stopAudioCapture() {
        if (this.captureDestination) {
            for (const [, entry] of this.captureInputSources) {
                try {
                    entry.source.disconnect();
                    entry.gain.disconnect();
                }
                catch (_) {
                    // already disconnected
                }
            }
            this.captureInputSources.clear();
            try {
                this.mainGainNode.disconnect(this.captureDestination);
            }
            catch (_) {
                // already disconnected
            }
            const tracks = this.captureDestination.stream.getAudioTracks();
            for (const track of tracks) {
                track.stop();
            }
            this.captureDestination = undefined;
            console.log('[AudioPlayer] Audio capture stopped');
        }
    }
}
